import { useEffect } from 'react';

const PING_INTERVAL_MS = 30 * 1000;
const SESSION_STORAGE_KEY =
  'bgq_presence_session_id';

function getApiBase() {
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : '/api';
}

function createSessionId() {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch (error) {
    // Используем запасной вариант ниже.
  }

  return [
    Date.now().toString(36),
    Math.random().toString(36).slice(2),
    Math.random().toString(36).slice(2),
  ].join('-');
}

function getSessionId() {
  try {
    const saved = sessionStorage.getItem(
      SESSION_STORAGE_KEY
    );

    if (saved) {
      return saved;
    }

    const created = createSessionId();

    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      created
    );

    return created;
  } catch (error) {
    return createSessionId();
  }
}

export default function PresenceHeartbeat({
  token,
}) {
  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const sessionId = getSessionId();
    let intervalId = null;
    let stopped = false;
    let offlineSent = false;

    const requestBody = JSON.stringify({
      sessionId,
    });

    const sendPing = async () => {
      if (stopped) {
        return;
      }

      offlineSent = false;

      try {
        await fetch(
          `${getApiBase()}/presence/ping`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type':
                'application/json',
            },
            body: requestBody,
            cache: 'no-store',
          }
        );
      } catch (error) {
        console.warn(
          '[PresenceHeartbeat] ping failed:',
          error
        );
      }
    };

    const sendOffline = () => {
      if (offlineSent) {
        return;
      }

      offlineSent = true;

      try {
        void fetch(
          `${getApiBase()}/presence/offline`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type':
                'application/json',
            },
            body: requestBody,
            keepalive: true,
            cache: 'no-store',
          }
        );
      } catch (error) {
        // При закрытии вкладки ошибку уже негде показывать.
      }
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible'
      ) {
        void sendPing();
      }
    };

    const handleFocus = () => {
      void sendPing();
    };

    const handlePageShow = () => {
      offlineSent = false;
      void sendPing();
    };

    void sendPing();

    intervalId = window.setInterval(
      sendPing,
      PING_INTERVAL_MS
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    window.addEventListener(
      'focus',
      handleFocus
    );

    window.addEventListener(
      'pageshow',
      handlePageShow
    );

    window.addEventListener(
      'pagehide',
      sendOffline
    );

    window.addEventListener(
      'beforeunload',
      sendOffline
    );

    return () => {
      stopped = true;
      sendOffline();

      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      window.removeEventListener(
        'focus',
        handleFocus
      );

      window.removeEventListener(
        'pageshow',
        handlePageShow
      );

      window.removeEventListener(
        'pagehide',
        sendOffline
      );

      window.removeEventListener(
        'beforeunload',
        sendOffline
      );
    };
  }, [token]);

  return null;
}
